import { readItem, updateItem } from '@directus/sdk';
import type { CollectionType, NestedPartial, QueryItem, RegularCollections } from '@directus/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Schema } from '../../../../types/DirectusSchema';
import { queryKeysFactory } from '../../lib/queryKeysFactory.ts';
import { directusClient } from '../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../services/tanstackQueryClient.ts';

export const useDirectusCollectionDetail = <
  Collection extends RegularCollections<Schema>,
  TQuery extends QueryItem<Schema, CollectionType<Schema, Collection>>,
>(
  collection: Collection,
  key?: string | number,
  query?: TQuery,
  options?: {
    onMutationSuccess?: () => void;
  },
) => {
  const { data, isPending: queryIsPending } = useQuery({
    staleTime: 60 * (60 * 1000), // 60 mins
    queryKey: [...queryKeysFactory(collection).detail(key ?? ''), query],
    queryFn: async () => (key ? directusClient.request(readItem(collection, key, query)) : null),
    enabled: !!key,
  });

  const { mutate, isPending: mutationIsPending } = useMutation({
    mutationFn: (data: NestedPartial<Schema[Collection][number]>) =>
      directusClient.request(updateItem(collection, key ?? -1, data)),
    onSuccess: async () => {
      const queryKey = queryKeysFactory(collection).detail(key ?? '');

      await tanstackQueryClient.invalidateQueries({ queryKey });
      options?.onMutationSuccess?.();
    },
  });

  return {
    data,
    isPending: queryIsPending || mutationIsPending,
    mutate,
    queryIsPending,
    mutationIsPending,
  };
};
